/**
 * Prompt type definitions — shared between backend and frontend.
 *
 * Unlike LLM providers (which are admin-managed runtime data),
 * `PromptType` is a code-level enum: each value has hardcoded backend logic
 * (allowed placeholders, pipeline integration, response parsing). Adding a
 * new type requires code changes, not just an admin UI tweak.
 *
 * `OutputSchemaType` is the analogous code-level enum for the LLM *output*
 * shape a prompt asks for. Scorers register the schemas they consume; the
 * dispatch picks a scorer by `(QuestionType, outputSchemaType)`. See
 * `docs/FeatureDesigns/AssessmentAndMarking/ScorerDispatch.md`.
 *
 * Naming convention:
 *   - `PromptType` is lowercase snake_case verb-phrase, never versioned
 *     (e.g. `analyze_image_text_objective`).
 *   - `OutputSchemaType` is PascalCase noun-phrase, always ends in `V<N>`
 *     (e.g. `ObjectiveCorrectnessV1`).
 *
 * See `docs/FeatureDesigns/archive/prompt-types-rename.md` for the migration design.
 */

/**
 * Single source of truth for prompt types AND their human-readable labels.
 *
 * The order of keys defines the visual order in admin dropdowns and the
 * default Type-asc sort on the Prompts list — keep operationally similar
 * types adjacent (analyze flavors together, hint variants together, etc.).
 *
 * Adding a new prompt type = one entry here. The union, options array,
 * sort index, and label lookup are all derived.
 *
 * Pattern mirrors `questionTypes.ts`'s `QUESTION_TYPE_LABELS`.
 */
export const PROMPT_TYPE_LABELS = {
    analyze_image_text_objective: 'Analyze: Image+Text — Objective',
    analyze_image_text_freeform:  'Analyze: Image+Text — Freeform',
    analyze_text_objective:       'Analyze: Text — Objective',
    analyze_text_freeform:        'Analyze: Text — Freeform',
    analyze_visual_objective:     'Analyze: Visual — Objective',
    analyze_visual_freeform:      'Analyze: Visual — Freeform',
    analyze_visual_structured:    'Analyze: Visual — Structured (multi-part)',
    analyze_descriptive:          'Analyze: Descriptive (text, optional image)',
    extract_steps:                'Extract Steps',
    extract_visuals:              'Extract Visuals',
    hint_for_image_text:          'Hint (Image+Text)',
    hint_for_text:                'Hint (Text)',
    hint_for_visual:              'Hint (Visual)',
    textchat_greet:               'TextChat: Greet',
    textchat_guide:               'TextChat: Guide',
    textchat_summarize:           'TextChat: Summarize',
    voice_realtime:               'Voice: Realtime',
    concept_lab:                  'Concept Lab',
    detect_learning_moments:      'Detect Learning Moments',
    detect_learning_moments_sitting: 'Detect Learning Moments (whole sitting, one call)',
    chemistry_lab_mix:            'Chemistry Lab: Mix',
    chemistry_lab_excess:         'Chemistry Lab: Excess',
    compile_scene:                'Question Scene: Compile',
    eval_judge_answer_equivalence: 'Eval Judge: Answer Equivalence',
    eval_judge_assessment_quality: 'Eval Judge: Assessment Quality',
    generate_variations:          'Generate Question Variations (from PDF)',
    generate_variations_from_questions: 'Generate Question Variations (from questions)',
    extract_verbatim:             'Extract Questions from Pdf',
    extract_verbatim_text:        'Extract Questions from Docx',
    extract_verbatim_structured:      'Extract Multi-Part Questions from Pdf',
    extract_verbatim_text_structured: 'Extract Multi-Part Questions from Docx',
    validate_extraction:          'Validate Extraction (judge: transcription vs source)',
    locate_diagrams:              'Locate Diagrams (second reader: boxes on the pages with figures)',
    enrich_questions:             'Enrich Extracted Questions',
    classify_questions:           'Classify Extracted Questions (topic + difficulty, one call per subject)',
    split_solutions:              'Split Batch Solutions (student pages → questions)',
    read_answer:                  'Answer Second Read (the final answer only, off the uncropped page)',
    read_answer_list:             'Answer List Read (a marked answer-list page alone, answers only)',
    read_solution_pages:          'Solution Page Reader (each uploaded page once, no question context)',
    detect_page_orientation:      'Detect Page Orientation (upright rotation)',
    extract_mark_scheme:          'Extract Mark Scheme (document → per-question credit rubric)',
    extract_answer_key:           'Extract Answer Key (key document → per-question answer + worked solution)',
    mark_attempt_one_shot:        'Mark Attempt (whole scheme + whole attempt, one shot)',
    generate_question_session_report: 'Generate Question Session Report',
    cluster_misconceptions:       'Cluster Cohort Misconceptions (grouping only)',
    generate_test_report:         'Generate Test Report (prose over a computed ledger)',
} as const;

export type PromptType = keyof typeof PROMPT_TYPE_LABELS;

/** Ordered list of all PromptTypes. Order matches `PROMPT_TYPE_LABELS`'s
 *  insertion order — used as the visual order in admin Create dropdowns
 *  and the default sort key for the Prompts table. */
export const PROMPT_TYPES = Object.keys(PROMPT_TYPE_LABELS) as [PromptType, ...PromptType[]];

/** Human-readable label for a PromptType. Falls back to the raw type
 *  string for unrecognized values so unfamiliar types still render. */
export function promptTypeLabel(t: string): string {
    return (PROMPT_TYPE_LABELS as Record<string, string>)[t] ?? t;
}

/** Sort key for Type-asc ordering. Higher = later. Unknown types should
 *  sort to the end via the `?? Number.MAX_SAFE_INTEGER` pattern at the
 *  call site. */
export const PROMPT_TYPE_SORT_INDEX: Record<string, number> = (() => {
    const idx: Record<string, number> = {};
    PROMPT_TYPES.forEach((t, i) => { idx[t] = i; });
    return idx;
})();

/**
 * Identifies the JSON shape the LLM is asked to emit for a prompt.
 * Drives both schema validation (canonicalSchemas) and scorer dispatch.
 * The trailing `V<N>` is a defensive version suffix so a breaking shape
 * change can land as `V2` without disturbing prompts on the older shape.
 */
export type OutputSchemaType =
    | 'ObjectiveCorrectnessV1'        // MCQ, Numerical, Singleline — binary value/unit + steps
    | 'FreeformRubricV1'              // Freeform — content/reasoning rubric + steps
    | 'StructuredAssessmentV1'        // STRUCTURED questions — per-part records, freeform OR objective (MCQ/Numerical) per part
    | 'DescriptiveAnswerV1'           // Descriptive-subject written answers — one accuracy axis, parts not steps (biology first)
    | 'ExtractedStepsV1'              // Extraction step (student's solution steps)
    | 'VisualsV1'                     // Visual-content extraction (diagrams, matrices, molecules, FBDs)
    | 'HintV1'                        // Hint generation
    | 'EvalJudgeEquivalenceV1'            // LLM-judge answer-equivalence verdicts (batched)
    | 'EvalJudgeAssessmentQualityV1'      // LLM-judge per-step assessment-quality verdicts (batched)
    | 'ExtractedQuestionsV1'          // Verbatim question extraction from a worksheet (multi-page, with diagram boxes)
    | 'ExtractedQuestionsStructuredV1' // As above, plus lettered sub-parts on each question
    | 'MarkSchemeV1'                  // Mark-scheme transcription — per-question tariffs, accepted answers, M/A/B credit items
    | 'AnswerKeyV1'                   // Answer & solution key transcription — per question number, the printed answer and worked solution
    | 'MarkAttemptV1'                 // One-shot attempt marking — per question, the scheme's credit items WITH the model's met/not-met verdict
    | 'ExtractionValidationV1'        // Judge verdict on a transcription: per-question fidelity + questions missed entirely
    | 'DiagramBoxesV1'                // Diagram boxes only, per question number and page — the second reader of an extraction
    | 'TestReportV1'                  // Test report — PROSE ONLY over a ledger computed in code; the model states no figure of its own
    | 'MisconceptionClustersV1'       // Cohort misconception grouping — labelled groups of observation ids, NO counts (those are computed from the real rows)
    | 'ChemistryLabMixV1'             // Free-form chemistry lab — computed outcome for one substance/heat mix (color/gas/precipitate/temperature/pH + narration)
    | 'ChemistryLabExcessV1'          // Quantitative bench — how that outcome LOOKS when one reagent is left over (an overlay on the mix's own result)
    | 'SceneCompileV1'                // A question's setup on a bench, or why there is none — the model translates, the bench's engine answers
    | 'SolutionPagesV1'               // Uploaded solution pages read once each: per page, blocks of work under the number the student wrote, each a VisualsV1 reading
    | 'AnswerListV1';                 // One page marked as an answer list: the answers written against question numbers, copied, never judged

/** Runtime tuple of all canonical OutputSchemaType values — kept in lockstep with the union. */
export const OUTPUT_SCHEMA_TYPES = [
    'ObjectiveCorrectnessV1',
    'FreeformRubricV1',
    'StructuredAssessmentV1',
    'DescriptiveAnswerV1',
    'ExtractedStepsV1',
    'VisualsV1',
    'HintV1',
    'EvalJudgeEquivalenceV1',
    'EvalJudgeAssessmentQualityV1',
    'ExtractedQuestionsV1',
    'ExtractedQuestionsStructuredV1',
    'MarkSchemeV1',
    'AnswerKeyV1',
    'MarkAttemptV1',
    'MisconceptionClustersV1',
    'ExtractionValidationV1',
    'DiagramBoxesV1',
    'TestReportV1',
    'ChemistryLabMixV1',
    'ChemistryLabExcessV1',
    'SceneCompileV1',
    'SolutionPagesV1',
    'AnswerListV1',
] as const satisfies ReadonlyArray<OutputSchemaType>;

/**
 * Legacy snake_case OutputSchemaType values still present in Cosmos data
 * pre-migration. Reads normalize via `normalizeOutputSchemaType`; this set
 * is also accepted by `isOutputSchemaType` so request bodies carrying
 * legacy values during the transition pass validation.
 */
const LEGACY_OUTPUT_SCHEMA_TYPES = [
    'objective_correctness_v1',
    'freeform_rubric_v1',
    'extracted_steps_v1',
    'hint_v1',
] as const;

const LEGACY_TO_CANONICAL_OUTPUT_SCHEMA: Record<typeof LEGACY_OUTPUT_SCHEMA_TYPES[number], OutputSchemaType> = {
    objective_correctness_v1: 'ObjectiveCorrectnessV1',
    freeform_rubric_v1:       'FreeformRubricV1',
    extracted_steps_v1:       'ExtractedStepsV1',
    hint_v1:                  'HintV1',
};

/**
 * Normalize a possibly-legacy OutputSchemaType string to its canonical
 * PascalCase form. Returns `undefined` for unrecognized inputs so callers
 * can decide whether to throw or pass through.
 */
export function normalizeOutputSchemaType(value: unknown): OutputSchemaType | undefined {
    if (typeof value !== 'string') return undefined;
    if ((OUTPUT_SCHEMA_TYPES as ReadonlyArray<string>).includes(value)) {
        return value as OutputSchemaType;
    }
    if (value in LEGACY_TO_CANONICAL_OUTPUT_SCHEMA) {
        return LEGACY_TO_CANONICAL_OUTPUT_SCHEMA[value as typeof LEGACY_OUTPUT_SCHEMA_TYPES[number]];
    }
    return undefined;
}

/**
 * Type guard for runtime input (e.g. validating a request body). Accepts
 * both canonical PascalCase and legacy snake_case during the migration so
 * pre-migration clients/data don't fail validation.
 */
export function isOutputSchemaType(value: unknown): value is OutputSchemaType {
    return normalizeOutputSchemaType(value) !== undefined;
}

/**
 * Canonical mapping: `PromptType` → `OutputSchemaType`. `null` for types
 * whose schema hasn't been formalized yet (textchat_*, voice_realtime,
 * concept_lab, detect_learning_moments).
 */
export const PROMPT_OUTPUT_SCHEMA: Record<PromptType, OutputSchemaType | null> = {
    extract_steps:                'ExtractedStepsV1',
    extract_visuals:              'VisualsV1',
    analyze_image_text_objective: 'ObjectiveCorrectnessV1',
    analyze_image_text_freeform:  'FreeformRubricV1',
    analyze_text_objective:       'ObjectiveCorrectnessV1',
    analyze_text_freeform:        'FreeformRubricV1',
    analyze_visual_objective:     'ObjectiveCorrectnessV1',
    analyze_visual_freeform:      'FreeformRubricV1',
    analyze_visual_structured:    'StructuredAssessmentV1',
    analyze_descriptive:          'DescriptiveAnswerV1',
    hint_for_image_text:          'HintV1',
    hint_for_text:                'HintV1',
    hint_for_visual:              'HintV1',
    textchat_greet:               null,
    textchat_guide:               null,
    textchat_summarize:           null,
    voice_realtime:               null,
    concept_lab:                  null,
    detect_learning_moments:      null,
    detect_learning_moments_sitting: null,
    chemistry_lab_mix:            'ChemistryLabMixV1',
    chemistry_lab_excess:         'ChemistryLabExcessV1',
    // Checked by the bench's own engine against the answer key, never scored.
    compile_scene:                'SceneCompileV1',
    eval_judge_answer_equivalence: 'EvalJudgeEquivalenceV1',
    eval_judge_assessment_quality: 'EvalJudgeAssessmentQualityV1',
    // Generation, not scoring — output shape is handled by a call-time JSON
    // schema in the generate-question-variations job, not the scorer dispatch.
    generate_variations:          null,
    generate_variations_from_questions: null,
    // Verbatim extraction + its enrichment pass — output shape comes from the
    // canonical JSON schemas (typeSchemas/), not the scorer dispatch.
    extract_verbatim:             'ExtractedQuestionsV1',
    extract_verbatim_text:        'ExtractedQuestionsV1',
    // Same questions, plus `parts[]`. A DISTINCT schema rather than an optional
    // field on the flat one: the flat prompt must not advertise a property it is
    // told never to fill, and the absence convention below would otherwise make
    // every flat question carry an empty `parts` it had to reason about.
    extract_verbatim_structured:      'ExtractedQuestionsStructuredV1',
    extract_verbatim_text_structured: 'ExtractedQuestionsStructuredV1',
    // The judge's verdict on the two steps above. Declared rather than null for
    // the same reason as extract_mark_scheme: `null` reads downstream as "run
    // the analyze-family quality checks", which are meaningless for a verdict.
    validate_extraction:          'ExtractionValidationV1',
    // Boxes per question number, off the pages the text reading found figures
    // on. A prompt may ask for the whole extraction shape instead
    // (`outputSchemaType: ExtractedQuestionsV1`) — only the boxes are kept.
    locate_diagrams:              'DiagramBoxesV1',
    enrich_questions:             null,
    // Topic and difficulty only, folded onto the draft by the job handler.
    classify_questions:           null,
    // Solution split — output shape is a call-time JSON schema in
    // the solution-splitter job, not the scorer dispatch.
    split_solutions:              null,
    read_answer:                  null,
    // Declared for the same reason as read_solution_pages below.
    read_answer_list:             'AnswerListV1',
    // Page reader. Declared rather than null for the same reason as
    // extract_mark_scheme below: `null` reads downstream as "run the objective
    // AND freeform checks", which would judge a page transcription as if it
    // were an assessment. See solution-analysis/solution-page-reader-target.md.
    read_solution_pages:          'SolutionPagesV1',
    // Page-orientation probe — tiny call-time JSON schema attached in the
    // orientation prompt-type handler, not a canonical scorer schema.
    detect_page_orientation:      null,
    // Mark-scheme transcription. Declared rather than left null: `null` is not
    // "no schema" downstream — buildModelRuns leaves the run record unlabelled,
    // and the quality-check gate reads `!schema` as "run the objective AND
    // freeform checks", which are meaningless for a scheme transcription.
    // See AssessmentAndMarking/marking-scheme-upload.md.
    extract_mark_scheme:          'MarkSchemeV1',
    // Answer-key transcription. Declared, not null, for the same reason: the
    // analyze-family quality checks are meaningless for a transcribed key.
    // See assignments-tests/paper-answer-key-target.md.
    extract_answer_key:           'AnswerKeyV1',
    // One-shot attempt marking. Same reasoning as above — declared, not null, so
    // the run record is labelled and the analyze-family quality checks (which are
    // meaningless here) stay off.
    mark_attempt_one_shot:        'MarkAttemptV1',
    // Report synthesis — the handler parses the JSON report; validation/caps
    // happen in the operation (finalizeQuestionSessionReport), not scorer dispatch.
    generate_question_session_report: null,
    // Grouping only. Declared (not null) so the run record is labelled and the
    // analyze-family quality checks — which score a single student's solution —
    // stay off a call that reads a whole class's observations.
    cluster_misconceptions:       'MisconceptionClustersV1',
    generate_test_report:         'TestReportV1',
};

/** Resolve the canonical OutputSchemaType for a PromptType, or `undefined` if none. */
export function outputSchemaForType(t: PromptType): OutputSchemaType | undefined {
    return PROMPT_OUTPUT_SCHEMA[t] ?? undefined;
}

/**
 * Schemas whose parsed payload is an *extraction* result (lives on
 * ModelOutput.extractedData) rather than a Feedback (lives on
 * ModelOutput.assessmentDetails). Used by pipeline writers and admin
 * UI readers alike — adding a new extraction-shape schema means adding
 * it here once, and all extraction/analysis branching downstream
 * follows. See ParsedFeedbackBody.tsx for the read-side mirror.
 */
export const EXTRACTION_OUTPUT_SCHEMAS = new Set<OutputSchemaType>([
    'ExtractedStepsV1',
    'VisualsV1',
    'SolutionPagesV1',
]);

/** True when the given schema is an extraction schema (parsed payload
 *  rides on `extractedData`, no Feedback post-processing). */
export function isExtractionOutputSchema(s: OutputSchemaType | null | undefined): boolean {
    return !!s && EXTRACTION_OUTPUT_SCHEMAS.has(s);
}

/**
 * Pipeline step types. **Superset** of PromptType — every PromptType is a
 * valid StepType (LLM-driven steps), plus the compute steps that aren't
 * prompt-driven (currently the score_* family).
 *
 * Compute steps are pure functions over pipeline context; they have no
 * prompts, no providers, no temperature. The runtime invokes them via
 * registered ComputeStepHandlers (see typed-pipelines.md §3.3, §6).
 */
export type StepType =
    | PromptType
    | 'score_objective'             // (Question, Solution, ObjectiveCorrectnessV1) → ComputedScores
    | 'score_freeform'              // (Question, Solution, FreeformRubricV1)       → ComputedScores
    | 'score_structured'            // (Question, Solution, StructuredAssessmentV1) → ComputedScores + partResults (marks-weighted)
    | 'score_descriptive'           // (Question, Solution, DescriptiveAnswerV1)    → ComputedScores (accuracy axis only)
    | 'reconcile_answer_key'        // every reading of extract_answer_key          → agreed rows + unclear rows
    | 'merge_extraction_readings';  // every reading of extract_verbatim(_structured) → merged questions + agreement

/**
 * Reconcile steps: code steps that turn every reading of the step before them
 * (a run on a parallel set) into that step's result. Each names the step types
 * it may follow. See
 * docs/FeatureDesigns/PipelineInfra/reconciled-readings-target.md §2.3.
 */
export const RECONCILE_STEP_SOURCES: Readonly<Partial<Record<StepType, ReadonlyArray<StepType>>>> = {
    reconcile_answer_key: ['extract_answer_key'],
    merge_extraction_readings: ['extract_verbatim', 'extract_verbatim_structured'],
};

/**
 * Family classification for a StepType. One axis, exhaustively typed.
 * Adding a new StepType requires adding it to `STEP_TYPE_FAMILY` below
 * (the Record's exhaustiveness check makes this a compile-time error
 * if you forget) — that's the design property that lets every consumer
 * just call `stepTypeFamily(t) === 'analyze'` instead of enumerating
 * individual values.
 *
 * Replaces the previous `isAnalysisType` / `isHintType` / `isTextChatType`
 * / `isRealtimeVoiceType` / `isConceptLabType` / `isLearningMomentsType`
 * predicates plus `isComputeStepType` and the redundant
 * `STEP_OF_PROMPT` mapping. One mapping, one accessor.
 */
export type StepFamily =
    | 'analyze'
    | 'extract'
    | 'hint'
    | 'textchat'
    | 'voice'
    | 'concept_lab'
    | 'learning_moments'
    | 'eval_judge'   // LLM-as-judge eval scoring
    | 'generate'     // content authoring (generate_variations*) — not a pipeline step
    | 'marking'      // awards exam marks against an uploaded mark scheme
    | 'chemistry_lab' // free-form chemistry lab mix computation
    | 'compute';     // score_* — pure-function steps with no prompt/provider

export const STEP_TYPE_FAMILY: Record<StepType, StepFamily> = {
    extract_steps:                'extract',
    extract_visuals:              'extract',
    analyze_image_text_objective: 'analyze',
    analyze_image_text_freeform:  'analyze',
    analyze_text_objective:       'analyze',
    analyze_text_freeform:        'analyze',
    analyze_visual_objective:     'analyze',
    analyze_visual_freeform:      'analyze',
    analyze_visual_structured:    'analyze',
    analyze_descriptive:          'analyze',
    hint_for_image_text:          'hint',
    hint_for_text:                'hint',
    hint_for_visual:              'hint',
    textchat_greet:               'textchat',
    textchat_guide:               'textchat',
    textchat_summarize:           'textchat',
    voice_realtime:               'voice',
    concept_lab:                  'concept_lab',
    detect_learning_moments:      'learning_moments',
    detect_learning_moments_sitting: 'learning_moments',
    chemistry_lab_mix:            'chemistry_lab',
    chemistry_lab_excess:         'chemistry_lab',
    compile_scene:                'generate',
    eval_judge_answer_equivalence: 'eval_judge',
    eval_judge_assessment_quality: 'eval_judge',
    generate_variations:          'generate',
    generate_variations_from_questions: 'generate',
    extract_verbatim:             'generate',
    extract_verbatim_text:        'generate',
    extract_verbatim_structured:      'generate',
    extract_verbatim_text_structured: 'generate',
    validate_extraction:          'generate',
    locate_diagrams:              'generate',
    enrich_questions:             'generate',
    classify_questions:           'generate',
    split_solutions:              'generate',
    read_answer:                  'generate',
    read_answer_list:             'generate',
    read_solution_pages:          'generate',
    detect_page_orientation:      'generate',
    extract_mark_scheme:          'generate',
    extract_answer_key:           'generate',
    // Its own family rather than 'generate': it neither authors nor extracts
    // content, it awards marks — and the distinction is load-bearing, because
    // family is what `llmFeaturesForStepType` and the walker's handler-input
    // builder dispatch on.
    mark_attempt_one_shot:        'marking',
    // 'generate' rather than 'analyze': the walker's analyze branch threads a
    // single student's question/solution context, and this call has neither —
    // its unit is a whole cohort's observations.
    cluster_misconceptions:       'generate',
    generate_question_session_report: 'generate',
    generate_test_report:         'generate',
    score_objective:              'compute',
    score_freeform:               'compute',
    score_structured:             'compute',
    score_descriptive:            'compute',
    reconcile_answer_key:         'compute',
    merge_extraction_readings:    'compute',
};

/** Family accessor. Use `stepTypeFamily(t) === 'analyze'` etc. instead
 *  of enumerating individual stepTypes. */
export function stepTypeFamily(t: StepType): StepFamily {
    return STEP_TYPE_FAMILY[t];
}
